import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { MapContainer, TileLayer, Polygon, Polyline, Marker, useMapEvents, useMap } from 'react-leaflet';
import 'leaflet/dist/leaflet.css';
import L from 'leaflet';
import {
	AlertTriangle,
	Building2,
	Check,
	ChevronDown,
	Crosshair,
	ImagePlus,
	Info,
	Layers,
	Loader2,
	MapPin,
	Pencil,
	Plus,
	Redo2,
	RotateCcw,
	Save,
	Search,
	Shapes,
	Trash2,
	Undo2,
	X,
} from 'lucide-react';
import { BACKEND_URL } from '../../config';
import { useConfirm } from '../../compos/Common/ConfirmDialog';

/**
 * Organisations — create organisations, give them a logo, and draw the campus
 * perimeter for each.
 *
 * The saved polygon is served to the app, which runs the point-in-polygon test
 * itself when the user picks that organisation while filing a report (the
 * server only hands over coordinates). Everything here is a local draft:
 * nothing reaches the DB until "Save boundary" is pressed.
 *
 * Caps mirror src/utils/geofence.ts in the backend, which is authoritative.
 */

const MAX_AREAS = 5;
const MAX_POINTS_PER_AREA = 100;
const MAX_TOTAL_POINTS = 200;
const MIN_POINTS = 3;
const MAX_BUFFER_METERS = 2000;
const HISTORY_LIMIT = 120;
const MAX_LOGO_BYTES = 2 * 1024 * 1024;

// Falls back to IIT Delhi — the first organisation mapped — so an unmapped org
// opens somewhere useful instead of mid-ocean.
const DEFAULT_CENTER: [number, number] = [28.5449, 77.1926];
const DEFAULT_ZOOM = 15;

type LatLng = { lat: number; lng: number };
type DraftArea = { key: string; name?: string; ring: LatLng[] };

type SavedBoundary = {
	areas: { name?: string; ring: LatLng[] }[];
	bufferMeters: number;
	version: number;
	isActive: boolean;
	updatedAt?: string;
	updatedByUserId?: number | null;
};

type OrgRow = {
	key: string;
	fullName: string;
	logoUrl: string | null;
	hasLogo: boolean;
	/** Active authorities assigned to this org — shown in the delete warning. */
	authorityCount: number;
	boundary: SavedBoundary;
};

// ── Geometry ───────────────────────────────────────────────────────────────
// Deliberately duplicated from the backend's utils/geofence.ts: this copy only
// drives live editor feedback, the server re-validates on save and wins.

// const EARTH_R = 6378137;
// const toRad = (d: number) => (d * Math.PI) / 180;

// function haversine(a: LatLng, b: LatLng): number {
// 	const dLat = toRad(b.lat - a.lat);
// 	const dLng = toRad(b.lng - a.lng);
// 	const s =
// 		Math.sin(dLat / 2) ** 2 + Math.cos(toRad(a.lat)) * Math.cos(toRad(b.lat)) * Math.sin(dLng / 2) ** 2;
// 	return 2 * EARTH_R * Math.asin(Math.min(1, Math.sqrt(s)));
// }

// function ringPerimeter(ring: LatLng[]): number {
// 	if (ring.length < 2) return 0;
// 	let total = 0;
// 	for (let i = 0; i < ring.length; i++) total += haversine(ring[i], ring[(i + 1) % ring.length]);
// 	return total;
// }

/** Spherical-excess area in m². Sign depends on winding, so take the absolute. */
// function ringArea(ring: LatLng[]): number {
// 	if (ring.length < 3) return 0;
// 	let sum = 0;
// 	for (let i = 0; i < ring.length; i++) {
// 		const p1 = ring[i];
// 		const p2 = ring[(i + 1) % ring.length];
// 		sum += toRad(p2.lng - p1.lng) * (2 + Math.sin(toRad(p1.lat)) + Math.sin(toRad(p2.lat)));
// 	}
// 	return Math.abs((sum * EARTH_R * EARTH_R) / 2);
// }

function orientation(a: LatLng, b: LatLng, c: LatLng): number {
	const v = (b.lng - a.lng) * (c.lat - a.lat) - (b.lat - a.lat) * (c.lng - a.lng);
	if (v > 1e-12) return 1;
	if (v < -1e-12) return -1;
	return 0;
}

function onSegment(a: LatLng, b: LatLng, p: LatLng): boolean {
	return (
		Math.min(a.lat, b.lat) - 1e-12 <= p.lat &&
		p.lat <= Math.max(a.lat, b.lat) + 1e-12 &&
		Math.min(a.lng, b.lng) - 1e-12 <= p.lng &&
		p.lng <= Math.max(a.lng, b.lng) + 1e-12
	);
}

function segmentsIntersect(p1: LatLng, p2: LatLng, p3: LatLng, p4: LatLng): boolean {
	const o1 = orientation(p1, p2, p3);
	const o2 = orientation(p1, p2, p4);
	const o3 = orientation(p3, p4, p1);
	const o4 = orientation(p3, p4, p2);
	if (o1 !== o2 && o3 !== o4) return true;
	if (o1 === 0 && onSegment(p1, p2, p3)) return true;
	if (o2 === 0 && onSegment(p1, p2, p4)) return true;
	if (o3 === 0 && onSegment(p3, p4, p1)) return true;
	if (o4 === 0 && onSegment(p3, p4, p2)) return true;
	return false;
}

/**
 * Even-odd ray casting — what the app uses — reads a self-crossing ring as
 * partly inside-out, so a bow-tie perimeter must never be saved. Returns the
 * offending edge pair, or null when the ring is simple.
 */
function findSelfIntersection(ring: LatLng[]): { a: number; b: number } | null {
	const n = ring.length;
	if (n < 4) return null;
	for (let i = 0; i < n; i++) {
		for (let j = i + 1; j < n; j++) {
			if (j === i + 1) continue;
			if (i === 0 && j === n - 1) continue;
			if (segmentsIntersect(ring[i], ring[(i + 1) % n], ring[j], ring[(j + 1) % n])) {
				return { a: i, b: j };
			}
		}
	}
	return null;
}

// ── Outlines from OpenStreetMap ───────────────────────────────────────────
// Place search asks Nominatim (OpenStreetMap) for each result's outline as
// GeoJSON. Campuses mapped in OSM as a polygon (IIT Delhi is) come back as a
// ready-made perimeter; places mapped only as a point (e.g. AIIA Delhi) just
// move the map, and the admin draws by hand. Google Maps has no API that
// returns campus outlines, which is why this uses OSM.

type PlaceResult = {
	id: string;
	name: string;
	detail: string;
	lat: number;
	lng: number;
	/** Outer rings only (holes are irrelevant to "is this inside campus"). */
	rings: LatLng[][];
};

/** GeoJSON Polygon/MultiPolygon → outer rings as {lat,lng}, open (no repeated closing point). */
function ringsFromGeoJson(g: any): LatLng[][] {
	const polys: number[][][][] =
		g?.type === 'Polygon' ? [g.coordinates] : g?.type === 'MultiPolygon' ? g.coordinates : [];
	return polys
		.map((poly) => {
			const ring: LatLng[] = [];
			for (const [lng, lat] of poly?.[0] ?? []) {
				const p = { lat: round6(lat), lng: round6(lng) };
				const prev = ring[ring.length - 1];
				if (!prev || prev.lat !== p.lat || prev.lng !== p.lng) ring.push(p);
			}
			const first = ring[0];
			const last = ring[ring.length - 1];
			if (ring.length > 1 && first.lat === last.lat && first.lng === last.lng) ring.pop();
			return ring;
		})
		.filter((r) => r.length >= MIN_POINTS);
}

/** Douglas–Peucker on an open polyline, in local metres around lat0. */
function dpKeep(pts: LatLng[], tolM: number, lat0: number): LatLng[] {
	if (pts.length <= 2) return pts;
	const kx = 111320 * Math.cos((lat0 * Math.PI) / 180);
	const ky = 111320;
	const a = pts[0];
	const b = pts[pts.length - 1];
	const ax = a.lng * kx;
	const ay = a.lat * ky;
	const dx = b.lng * kx - ax;
	const dy = b.lat * ky - ay;
	const len2 = dx * dx + dy * dy;
	let maxD = -1;
	let idx = 0;
	for (let i = 1; i < pts.length - 1; i++) {
		const px = pts[i].lng * kx - ax;
		const py = pts[i].lat * ky - ay;
		let t = len2 ? (px * dx + py * dy) / len2 : 0;
		t = Math.max(0, Math.min(1, t));
		const d = Math.hypot(px - t * dx, py - t * dy);
		if (d > maxD) {
			maxD = d;
			idx = i;
		}
	}
	if (maxD <= tolM) return [a, b];
	const left = dpKeep(pts.slice(0, idx + 1), tolM, lat0);
	const right = dpKeep(pts.slice(idx), tolM, lat0);
	return [...left.slice(0, -1), ...right];
}

/**
 * Shrinks a closed ring to at most maxPts points, raising the tolerance until
 * it fits and stays non-self-intersecting (the server rejects crossing rings).
 * Returns null if no simple ring that small exists.
 */
function simplifyRing(ring: LatLng[], maxPts: number): LatLng[] | null {
	if (ring.length <= maxPts && !findSelfIntersection(ring)) return ring;
	if (maxPts < MIN_POINTS) return null;
	const lat0 = ring.reduce((s, p) => s + p.lat, 0) / ring.length;
	// Split the closed ring at its two mutually farthest-ish points so each half
	// is an open polyline Douglas–Peucker can work on.
	let far = 0;
	let farD = -1;
	for (let i = 1; i < ring.length; i++) {
		const d = Math.hypot(ring[i].lat - ring[0].lat, ring[i].lng - ring[0].lng);
		if (d > farD) {
			farD = d;
			far = i;
		}
	}
	const halfA = ring.slice(0, far + 1);
	const halfB = [...ring.slice(far), ring[0]];
	for (let tol = 0.5; tol < 2000; tol *= 1.4) {
		const out = [...dpKeep(halfA, tol, lat0).slice(0, -1), ...dpKeep(halfB, tol, lat0).slice(0, -1)];
		if (out.length < MIN_POINTS) return null;
		if (out.length <= maxPts && !findSelfIntersection(out)) return out;
	}
	return null;
}

// function formatArea(m2: number): string {
// 	if (m2 <= 0) return '—';
// 	if (m2 >= 1_000_000) return `${(m2 / 1_000_000).toFixed(2)} km²`;
// 	if (m2 >= 10_000) return `${(m2 / 10_000).toFixed(2)} ha`;
// 	return `${Math.round(m2).toLocaleString()} m²`;
// }

// function formatLength(m: number): string {
// 	if (m <= 0) return '—';
// 	return m >= 1000 ? `${(m / 1000).toFixed(2)} km` : `${Math.round(m)} m`;
// }

const round6 = (n: number) => Number(n.toFixed(6));

/** dd/mm/yyyy hh:mm am/pm — the format used across this panel. */
function formatDateTime(iso?: string | null): string {
	if (!iso) return '';
	const d = new Date(iso);
	if (Number.isNaN(d.getTime())) return '';
	const pad = (n: number) => String(n).padStart(2, '0');
	const hours24 = d.getHours();
	const suffix = hours24 >= 12 ? 'pm' : 'am';
	const hours12 = hours24 % 12 || 12;
	return `${pad(d.getDate())}/${pad(d.getMonth() + 1)}/${d.getFullYear()} ${pad(hours12)}:${pad(d.getMinutes())} ${suffix}`;
}

let areaKeySeq = 0;
const nextAreaKey = () => `area-${Date.now()}-${areaKeySeq++}`;

const logoSrc = (org: { logoUrl: string | null } | null | undefined) =>
	org?.logoUrl ? `${BACKEND_URL}${org.logoUrl}` : null;

/**
 * Logo with a graceful fallback. A stored logo can outlive its file (a blob
 * removed by hand, a dev machine that never synced them down), and a broken
 * <img> is a worse answer than the generic icon.
 */
const OrgLogo = ({ src, size, className = '' }: { src: string | null; size: number; className?: string }) => {
	const [failed, setFailed] = useState(false);
	useEffect(() => setFailed(false), [src]);

	if (!src || failed) {
		return <Building2 className={`text-gray-400 shrink-0 ${className}`} style={{ width: size, height: size }} />;
	}
	return (
		<img
			src={src}
			alt=''
			onError={() => setFailed(true)}
			className={`rounded-full object-cover shrink-0 bg-gray-50 ${className}`}
			style={{ width: size, height: size }}
		/>
	);
};

function toDraft(saved: SavedBoundary | null | undefined): DraftArea[] {
	if (!saved || !Array.isArray(saved.areas)) return [];
	return saved.areas.map((a) => ({
		key: nextAreaKey(),
		name: a.name,
		ring: (a.ring || []).map((p) => ({ lat: p.lat, lng: p.lng })),
	}));
}

/** Stable string used purely for dirty-checking against the saved version. */
function fingerprint(areas: DraftArea[], bufferMeters: number, isActive: boolean): string {
	return JSON.stringify({
		areas: areas
			// Freshly-added-but-undrawn areas aren't a change worth saving.
			.filter((a) => a.ring.length > 0)
			.map((a) => ({ name: a.name ?? null, ring: a.ring.map((p) => [round6(p.lat), round6(p.lng)]) })),
		bufferMeters,
		isActive,
	});
}

// ── Map plumbing ───────────────────────────────────────────────────────────

const vertexIcon = L.divIcon({
	className: 'orgmap-vertex',
	html: '<div style="width:12px;height:12px;border-radius:50%;background:#2563eb;border:2px solid #fff;box-shadow:0 1px 3px rgba(0,0,0,.45);"></div>',
	iconSize: [12, 12],
	iconAnchor: [6, 6],
});

const firstVertexIcon = L.divIcon({
	className: 'orgmap-vertex',
	html: '<div style="width:14px;height:14px;border-radius:50%;background:#16a34a;border:2px solid #fff;box-shadow:0 1px 3px rgba(0,0,0,.45);"></div>',
	iconSize: [14, 14],
	iconAnchor: [7, 7],
});

const midpointIcon = L.divIcon({
	className: 'orgmap-midpoint',
	html: '<div style="width:9px;height:9px;border-radius:50%;background:rgba(255,255,255,.9);border:2px solid #2563eb;"></div>',
	iconSize: [9, 9],
	iconAnchor: [4.5, 4.5],
});

/** Adds a vertex on click, but ignores clicks that were really a map drag. */
const ClickToAdd = ({ onAdd }: { onAdd: (p: LatLng) => void }) => {
	const down = useRef<{ x: number; y: number } | null>(null);
	useMapEvents({
		mousedown(e) {
			down.current = { x: e.originalEvent.clientX, y: e.originalEvent.clientY };
		},
		click(e) {
			if (down.current) {
				const dx = e.originalEvent.clientX - down.current.x;
				const dy = e.originalEvent.clientY - down.current.y;
				if (Math.sqrt(dx * dx + dy * dy) > 5) return;
			}
			onAdd({ lat: e.latlng.lat, lng: e.latlng.lng });
		},
	});
	return null;
};

/**
 * Imperative handle so toolbar buttons can move the map.
 *
 * The cleanup matters: react-leaflet destroys the Leaflet instance when
 * MapContainer unmounts, and calling setView/fitBounds on a destroyed map
 * throws (its panes are gone). Clearing the ref keeps callers from ever
 * holding a dead map.
 */
const MapHandle = ({ onReady }: { onReady: (map: L.Map | null) => void }) => {
	const map = useMap();
	useEffect(() => {
		onReady(map);
		return () => onReady(null);
	}, [map, onReady]);
	return null;
};

/** True only while the map is mounted and usable. */
function mapIsAlive(map: L.Map | null): map is L.Map {
	if (!map) return false;
	try {
		return Boolean((map as any)._loaded) && Boolean(map.getContainer()?.isConnected);
	} catch {
		return false;
	}
}

/** Enables zoom via Ctrl (or Meta) + scroll wheel, while allowing normal scroll to scroll the page. */
const CtrlScrollZoom = () => {
	const map = useMap();
	const [showHint, setShowHint] = useState(false);
	const hintTimeoutRef = useRef<NodeJS.Timeout | null>(null);

	useEffect(() => {
		map.scrollWheelZoom.disable();
		const container = map.getContainer();

		let accumulatedDelta = 0;
		let lastZoomTime = 0;

		const handleWheel = (e: WheelEvent) => {
			if (e.ctrlKey || e.metaKey) {
				e.preventDefault();
				const now = Date.now();
				accumulatedDelta += e.deltaY;

				if (now - lastZoomTime > 60 || Math.abs(accumulatedDelta) >= 60) {
					const mouseLatLng = map.mouseEventToLatLng(e);
					const currentZoom = map.getZoom();

					if (accumulatedDelta < 0 && currentZoom < (map.getMaxZoom() ?? 20)) {
						map.setZoomAround(mouseLatLng, currentZoom + 1);
						lastZoomTime = now;
						accumulatedDelta = 0;
					} else if (accumulatedDelta > 0 && currentZoom > (map.getMinZoom() ?? 1)) {
						map.setZoomAround(mouseLatLng, currentZoom - 1);
						lastZoomTime = now;
						accumulatedDelta = 0;
					}
				}
			} else {
				setShowHint(true);
				if (hintTimeoutRef.current) clearTimeout(hintTimeoutRef.current);
				hintTimeoutRef.current = setTimeout(() => {
					setShowHint(false);
				}, 500);
			}
		};

		container.addEventListener('wheel', handleWheel, { passive: false });

		return () => {
			container.removeEventListener('wheel', handleWheel);
			if (hintTimeoutRef.current) clearTimeout(hintTimeoutRef.current);
		};
	}, [map]);

	if (!showHint) return null;

	const isMac = typeof navigator !== 'undefined' && /Mac|iPod|iPhone|iPad/.test(navigator.platform);

	return (
		<div className='absolute inset-0 z-[1000] pointer-events-none flex items-center justify-center bg-black/15 backdrop-blur-[0.5px] transition-opacity duration-200'>
			<div className='bg-gray-900/80 backdrop-blur-sm text-white font-medium text-xs px-3 py-1.5 rounded-lg shadow-md border border-white/10 flex items-center gap-1.5'>
				<Info className='w-3.5 h-3.5 text-blue-400 shrink-0' />
				<span>Use {isMac ? '⌘' : 'Ctrl'} + scroll to zoom</span>
			</div>
		</div>
	);
};

// Shared shell for the left-rail cards, so every box has the same padding and
// left-aligned heading treatment.
const RailCard = ({
	title,
	action,
	children,
}: {
	title: string;
	action?: React.ReactNode;
	children: React.ReactNode;
}) => (
	<div className='bg-white rounded-2xl shadow-sm border border-gray-100 p-4'>
		<div className='flex items-center justify-between gap-2 mb-3'>
			<h2 className='text-xs font-bold uppercase tracking-wide text-gray-500 text-left m-0'>{title}</h2>
			{action}
		</div>
		{children}
	</div>
);

// ── Panel ──────────────────────────────────────────────────────────────────

const OrganisationMapping: React.FC = () => {
	const [orgs, setOrgs] = useState<OrgRow[]>([]);
	const [loading, setLoading] = useState(true);
	const [loadError, setLoadError] = useState('');
	const [selectedKey, setSelectedKey] = useState<string>(() => sessionStorage.getItem('orgMappingSelectedKey') ?? '');
	const [orgMenuOpen, setOrgMenuOpen] = useState(false);

	// Draft state — never written back to the server until Save. Geometry and
	// its undo history live in ONE state object so every transition is a pure
	// update (no setState nested inside another updater, which React is free to
	// run twice).
	const [geom, setGeom] = useState<{ areas: DraftArea[]; past: DraftArea[][]; future: DraftArea[][] }>({
		areas: [],
		past: [],
		future: [],
	});
	const { areas, past, future } = geom;
	const [activeIdx, setActiveIdx] = useState(0);
	const [bufferMeters, setBufferMeters] = useState<number>(20);
	const [guardActive, setGuardActive] = useState(true);
	const [baseline, setBaseline] = useState<string>('');

	const [saving, setSaving] = useState(false);
	const [satellite, setSatellite] = useState(false);
	const [searchText, setSearchText] = useState('');
	const [searching, setSearching] = useState(false);
	// Search results (dropdown) and the outline being previewed on the map.
	const [placeResults, setPlaceResults] = useState<PlaceResult[] | null>(null);
	const [preview, setPreview] = useState<PlaceResult | null>(null);
	const searchBoxRef = useRef<HTMLDivElement>(null);

	useEffect(() => {
		if (!placeResults) return;
		const onDown = (e: MouseEvent) => {
			if (searchBoxRef.current && !searchBoxRef.current.contains(e.target as Node)) setPlaceResults(null);
		};
		document.addEventListener('mousedown', onDown);
		return () => document.removeEventListener('mousedown', onDown);
	}, [placeResults]);

	// Add / edit organisation modal.
	const [orgForm, setOrgForm] = useState<{
		mode: 'create' | 'edit';
		key: string;
		fullName: string;
		file: File | null;
		preview: string | null;
	} | null>(null);
	const [savingOrg, setSavingOrg] = useState(false);

	// Two-step delete for areas: the first click arms it (× becomes ✓), the
	// second commits. Holds the area's key, not its index, so reordering can't
	// point the confirmation at the wrong area.
	const [armedAreaKey, setArmedAreaKey] = useState<string | null>(null);
	const dragLinesRef = useRef<L.Polyline[]>([]);

	const { confirm, confirmDialog } = useConfirm();

	// Toast — same treatment as the other admin panels.
	const [toast, setToast] = useState<{ message: string; type: 'success' | 'error' } | null>(null);
	const toastTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
	const showToast = useCallback((message: string, type: 'success' | 'error' = 'success') => {
		if (toastTimerRef.current) clearTimeout(toastTimerRef.current);
		setToast({ message, type });
		toastTimerRef.current = setTimeout(() => setToast(null), 3500);
	}, []);
	useEffect(() => () => { if (toastTimerRef.current) clearTimeout(toastTimerRef.current); }, []);

	const mapRef = useRef<L.Map | null>(null);
	const orgMenuRef = useRef<HTMLDivElement>(null);

	const selectedOrg = orgs.find((o) => o.key === selectedKey) || null;
	const activeArea = areas[activeIdx] || null;

	const totalPoints = useMemo(() => areas.reduce((n, a) => n + a.ring.length, 0), [areas]);
	const dirty = useMemo(
		() => Boolean(selectedKey) && fingerprint(areas, bufferMeters, guardActive) !== baseline,
		[areas, bufferMeters, guardActive, baseline, selectedKey],
	);

	// Per-area validity, computed live so Save can be blocked before a round trip.
	// A brand-new area with no points yet isn't an error — it's just undrawn, and
	// it gets dropped on save.
	const problems = useMemo(() => {
		const out: string[] = [];
		areas.forEach((a, i) => {
			if (a.ring.length === 0) return;
			const label = a.name?.trim() || `Area ${i + 1}`;
			if (a.ring.length < MIN_POINTS) {
				out.push(`${label}: needs at least ${MIN_POINTS} points (has ${a.ring.length}).`);
				return;
			}
			if (a.ring.length > MAX_POINTS_PER_AREA) {
				out.push(`${label}: over the ${MAX_POINTS_PER_AREA}-point limit.`);
			}
			const x = findSelfIntersection(a.ring);
			if (x) out.push(`${label}: the perimeter crosses itself (edge ${x.a + 1} × edge ${x.b + 1}). Untangle it.`);
		});
		if (totalPoints > MAX_TOTAL_POINTS) out.push(`${totalPoints} points total — the limit is ${MAX_TOTAL_POINTS}.`);
		return out;
	}, [areas, totalPoints]);

	// Saving with zero areas is how a geofence is cleared, so an empty draft is
	// a legitimate thing to submit.
	const canSave = dirty && problems.length === 0 && !saving;

	// ── Loading ──────────────────────────────────────────────────────────────

	const loadOrganisations = useCallback(async () => {
		// Only the very first load takes over the page. A refresh after
		// creating/editing an organisation must NOT swap the editor out for a
		// spinner: that unmounts the map, and anything still holding the old
		// Leaflet instance would be talking to a destroyed object.
		setOrgs((prev) => {
			if (prev.length === 0) setLoading(true);
			return prev;
		});
		setLoadError('');
		try {
			const res = await fetch(`${BACKEND_URL}/users/organisation/list`, {
				method: 'GET',
				credentials: 'include',
			});
			if (!res.ok) throw new Error(`HTTP ${res.status}`);
			const data: OrgRow[] = await res.json();
			setOrgs(data);
			setSelectedKey((prev) => {
				const stored = sessionStorage.getItem('orgMappingSelectedKey');
				const candidate = prev || stored || '';
				return candidate && data.some((o) => o.key === candidate) ? candidate : data[0]?.key || '';
			});
		} catch (e) {
			console.error(e);
			setLoadError('Could not load organisations. Check your connection and retry.');
		} finally {
			setLoading(false);
		}
	}, []);

	useEffect(() => {
		loadOrganisations();
	}, [loadOrganisations]);

	useEffect(() => {
		if (selectedKey) {
			sessionStorage.setItem('orgMappingSelectedKey', selectedKey);
		}
	}, [selectedKey]);

	/** Load one organisation's saved boundary into the draft, discarding edits. */
	const adoptOrg = useCallback((org: OrgRow | null) => {
		const draft = toDraft(org?.boundary);
		const buffer = org?.boundary?.bufferMeters ?? 20;
		const active = org?.boundary?.isActive ?? true;
		setGeom({ areas: draft, past: [], future: [] });
		setActiveIdx(0);
		setBufferMeters(buffer);
		setGuardActive(active);
		setBaseline(fingerprint(draft, buffer, active));
	}, []);

	useEffect(() => {
		if (!selectedKey || orgs.length === 0) return;
		const target = orgs.find((o) => o.key === selectedKey);
		if (target) {
			adoptOrg(target);
		}
	}, [selectedKey, orgs, adoptOrg]);

	// Frame whatever the newly selected org has saved.
	useEffect(() => {
		const map = mapRef.current;
		if (!mapIsAlive(map)) return;
		const pts = areas.flatMap((a) => a.ring);
		if (pts.length === 0) {
			map.setView(DEFAULT_CENTER, DEFAULT_ZOOM, { animate: false });
			return;
		}
		// Not animated: switching organisation is a jump between unrelated
		// campuses, so flying there wastes a second and can wedge mid-flight.
		map.fitBounds(L.latLngBounds(pts.map((p) => [p.lat, p.lng] as [number, number])), {
			padding: [40, 40],
			animate: false,
		});
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [selectedKey, baseline]);

	// ── Mutation + history ───────────────────────────────────────────────────

	/** Applies a geometry change and records one undo step. */
	const mutate = useCallback((fn: (prev: DraftArea[]) => DraftArea[]) => {
		setGeom((g) => {
			const next = fn(g.areas);
			if (next === g.areas) return g;
			return { areas: next, past: [...g.past, g.areas].slice(-HISTORY_LIMIT), future: [] };
		});
	}, []);

	const undo = useCallback(() => {
		setGeom((g) =>
			g.past.length === 0
				? g
				: {
					areas: g.past[g.past.length - 1],
					past: g.past.slice(0, -1),
					future: [g.areas, ...g.future].slice(0, HISTORY_LIMIT),
				},
		);
	}, []);

	const redo = useCallback(() => {
		setGeom((g) =>
			g.future.length === 0
				? g
				: {
					areas: g.future[0],
					past: [...g.past, g.areas].slice(-HISTORY_LIMIT),
					future: g.future.slice(1),
				},
		);
	}, []);

	useEffect(() => {
		const onKey = (e: KeyboardEvent) => {
			const target = e.target as HTMLElement | null;
			if (target && ['INPUT', 'TEXTAREA', 'SELECT'].includes(target.tagName)) return;
			const mod = e.ctrlKey || e.metaKey;
			if (mod && e.key.toLowerCase() === 'z' && !e.shiftKey) {
				e.preventDefault();
				undo();
			} else if (mod && (e.key.toLowerCase() === 'y' || (e.key.toLowerCase() === 'z' && e.shiftKey))) {
				e.preventDefault();
				redo();
			}
		};
		window.addEventListener('keydown', onKey);
		return () => window.removeEventListener('keydown', onKey);
	}, [undo, redo]);

	// Browser-level guard so a refresh or tab close can't silently bin the draft.
	useEffect(() => {
		if (!dirty) return;
		const onBeforeUnload = (e: BeforeUnloadEvent) => {
			e.preventDefault();
			e.returnValue = '';
		};
		window.addEventListener('beforeunload', onBeforeUnload);
		return () => window.removeEventListener('beforeunload', onBeforeUnload);
	}, [dirty]);

	useEffect(() => {
		const onClickOutside = (e: MouseEvent) => {
			if (orgMenuRef.current && !orgMenuRef.current.contains(e.target as Node)) setOrgMenuOpen(false);
		};
		if (orgMenuOpen) document.addEventListener('mousedown', onClickOutside);
		return () => document.removeEventListener('mousedown', onClickOutside);
	}, [orgMenuOpen]);

	// ── Editing actions ──────────────────────────────────────────────────────

	const addPoint = useCallback(
		(p: LatLng) => {
			// Cap checks run here, not inside the state updater, so they can raise
			// a message without making the update impure.
			const idx = areas.length === 0 ? 0 : Math.min(activeIdx, areas.length - 1);
			if ((areas[idx]?.ring.length ?? 0) >= MAX_POINTS_PER_AREA) {
				showToast(`This area is at the ${MAX_POINTS_PER_AREA}-point limit.`, 'error');
				return;
			}
			if (totalPoints >= MAX_TOTAL_POINTS) {
				showToast(`${MAX_TOTAL_POINTS}-point total limit reached across all areas.`, 'error');
				return;
			}
			mutate((prev) => {
				const list = prev.length === 0 ? [{ key: nextAreaKey(), ring: [] as LatLng[] }] : [...prev];
				const area = list[idx];
				list[idx] = { ...area, ring: [...area.ring, { lat: round6(p.lat), lng: round6(p.lng) }] };
				return list;
			});
		},
		[activeIdx, areas, totalPoints, mutate, showToast],
	);

	const moveVertex = useCallback(
		(areaIdx: number, vertexIdx: number, p: LatLng) => {
			mutate((prev) => {
				const list = [...prev];
				const ring = [...list[areaIdx].ring];
				ring[vertexIdx] = { lat: round6(p.lat), lng: round6(p.lng) };
				list[areaIdx] = { ...list[areaIdx], ring };
				return list;
			});
		},
		[mutate],
	);

	const removeVertex = useCallback(
		(areaIdx: number, vertexIdx: number) => {
			mutate((prev) => {
				const list = [...prev];
				const ring = list[areaIdx].ring.filter((_, i) => i !== vertexIdx);
				list[areaIdx] = { ...list[areaIdx], ring };
				return list;
			});
		},
		[mutate],
	);

	/** gmaps-style: the hollow handle on an edge becomes a real vertex. */
	const insertVertex = useCallback(
		(areaIdx: number, afterIdx: number, p: LatLng) => {
			if ((areas[areaIdx]?.ring.length ?? 0) >= MAX_POINTS_PER_AREA) {
				showToast(`This area is at the ${MAX_POINTS_PER_AREA}-point limit.`, 'error');
				return;
			}
			if (totalPoints >= MAX_TOTAL_POINTS) {
				showToast(`${MAX_TOTAL_POINTS}-point total limit reached across all areas.`, 'error');
				return;
			}
			mutate((prev) => {
				const list = [...prev];
				const ring = [...list[areaIdx].ring];
				ring.splice(afterIdx + 1, 0, { lat: round6(p.lat), lng: round6(p.lng) });
				list[areaIdx] = { ...list[areaIdx], ring };
				return list;
			});
		},
		[areas, totalPoints, mutate, showToast],
	);

	const removeLastPoint = useCallback(() => {
		mutate((prev) => {
			if (!prev[activeIdx] || prev[activeIdx].ring.length === 0) return prev;
			const list = [...prev];
			list[activeIdx] = { ...list[activeIdx], ring: list[activeIdx].ring.slice(0, -1) };
			return list;
		});
	}, [activeIdx, mutate]);

	const addArea = useCallback(() => {
		if (areas.length >= MAX_AREAS) {
			showToast(`At most ${MAX_AREAS} areas per organisation.`, 'error');
			return;
		}
		mutate((prev) => [...prev, { key: nextAreaKey(), ring: [] }]);
		setActiveIdx(areas.length);
	}, [areas.length, mutate, showToast]);

	const deleteArea = useCallback(
		(idx: number) => {
			mutate((prev) => prev.filter((_, i) => i !== idx));
			setActiveIdx((cur) => Math.max(0, cur >= idx ? cur - 1 : cur));
			setArmedAreaKey(null);
		},
		[mutate],
	);

	// Disarm a pending area delete as soon as attention moves elsewhere (event-driven on click outside).
	useEffect(() => {
		if (!armedAreaKey) return;

		const handleOutsideClick = (e: MouseEvent | TouchEvent) => {
			const target = e.target as HTMLElement | null;
			if (target && target.closest('[data-armed-btn="true"]')) {
				return;
			}
			setArmedAreaKey(null);
		};

		const timer = setTimeout(() => {
			window.addEventListener('click', handleOutsideClick, true);
			window.addEventListener('pointerdown', handleOutsideClick, true);
		}, 0);

		const fallbackTimer = setTimeout(() => setArmedAreaKey(null), 4000);

		return () => {
			clearTimeout(timer);
			clearTimeout(fallbackTimer);
			window.removeEventListener('click', handleOutsideClick, true);
			window.removeEventListener('pointerdown', handleOutsideClick, true);
		};
	}, [armedAreaKey]);
	useEffect(() => {
		setArmedAreaKey(null);
	}, [selectedKey]);

	const discardChanges = useCallback(async () => {
		if (
			dirty &&
			!(await confirm({
				title: 'Discard changes?',
				message: 'Your unsaved edits to this perimeter will be lost. The last saved version stays in place.',
				confirmLabel: 'Discard',
				danger: true,
			}))
		) {
			return;
		}
		adoptOrg(orgs.find((o) => o.key === selectedKey) || null);
		showToast('Changes discarded.');
	}, [dirty, adoptOrg, orgs, selectedKey, showToast, confirm]);

	const selectOrg = useCallback(
		async (key: string) => {
			if (key === selectedKey) {
				setOrgMenuOpen(false);
				return;
			}
			if (
				dirty &&
				!(await confirm({
					title: 'Unsaved changes',
					message: (
						<>
							You have unsaved edits to <strong>{selectedKey}</strong>. Switching organisation will discard them.
						</>
					),
					confirmLabel: 'Switch anyway',
					danger: true,
				}))
			) {
				return;
			}
			setSelectedKey(key);
			setOrgMenuOpen(false);
		},
		[dirty, selectedKey, confirm],
	);

	// ── Map helpers ──────────────────────────────────────────────────────────

	const onMapReady = useCallback((map: L.Map | null) => {
		mapRef.current = map;
	}, []);

	const fitToPoints = useCallback((pts: LatLng[]) => {
		const map = mapRef.current;
		if (pts.length === 0 || !mapIsAlive(map)) return;
		map.fitBounds(L.latLngBounds(pts.map((p) => [p.lat, p.lng] as [number, number])), {
			padding: [40, 40],
		});
	}, []);

	const fitToPolygon = useCallback(() => fitToPoints(areas.flatMap((a) => a.ring)), [areas, fitToPoints]);

	const searchPlace = useCallback(async () => {
		const q = searchText.trim();
		if (!q) return;
		setSearching(true);
		setPreview(null);
		try {
			// polygon_threshold trims OSM's outline server-side (~1 m), which keeps
			// the payload small without visibly changing a campus boundary.
			const res = await fetch(
				`https://nominatim.openstreetmap.org/search?format=jsonv2&limit=6&polygon_geojson=1&polygon_threshold=0.00001&q=${encodeURIComponent(q)}`,
			);
			const data = await res.json();
			const results: PlaceResult[] = (Array.isArray(data) ? data : []).map((r: any) => ({
				id: `${r.osm_type}-${r.osm_id}`,
				name: r.name || String(r.display_name || '').split(',')[0],
				detail: String(r.display_name || ''),
				lat: Number(r.lat),
				lng: Number(r.lon),
				rings: ringsFromGeoJson(r.geojson),
			}));
			if (results.length === 0) {
				showToast(`No place found for "${q}".`, 'error');
				setPlaceResults(null);
			} else {
				setPlaceResults(results);
			}
		} catch {
			showToast('Place search failed.', 'error');
		} finally {
			setSearching(false);
		}
	}, [searchText, showToast]);

	/** Picks a search result: previews its outline, or just goes there if it has none. */
	const choosePlace = useCallback(
		(place: PlaceResult) => {
			setPlaceResults(null);
			const map = mapRef.current;
			if (place.rings.length === 0) {
				setPreview(null);
				if (mapIsAlive(map)) map.setView([place.lat, place.lng], 17, { animate: false });
				showToast('No outline is mapped for this place. Draw it by clicking on the map.', 'error');
				return;
			}
			setPreview(place);
			if (mapIsAlive(map)) {
				const pts = place.rings.flat();
				map.fitBounds(L.latLngBounds(pts.map((p) => [p.lat, p.lng] as [number, number])), {
					padding: [40, 40],
					animate: false,
				});
			}
		},
		[showToast],
	);

	/**
	 * Turns the previewed outline into editable areas. 'replace' swaps the
	 * active area's points for it; 'add' appends it as new area(s). Big rings
	 * are simplified to fit the point caps. One undo step either way.
	 */
	const applyOutline = useCallback(
		(mode: 'replace' | 'add') => {
			if (!preview) return;
			const replacing = mode === 'replace' && areas.length > 0;
			const keptPoints = totalPoints - (replacing ? (areas[activeIdx]?.ring.length ?? 0) : 0);
			const slots = replacing ? 1 + (MAX_AREAS - areas.length) : MAX_AREAS - areas.length;
			if (slots <= 0) {
				showToast(`At most ${MAX_AREAS} areas per organisation.`, 'error');
				return;
			}

			// Largest rings first: a campus is its main block plus small outliers.
			const rings = [...preview.rings].sort((a, b) => b.length - a.length).slice(0, slots);
			let budget = MAX_TOTAL_POINTS - keptPoints;
			const fitted: LatLng[][] = [];
			const original = rings.reduce((n, r) => n + r.length, 0);
			for (const ring of rings) {
				const cap = Math.min(MAX_POINTS_PER_AREA, budget);
				const out = simplifyRing(ring, cap);
				if (!out) continue;
				fitted.push(out);
				budget -= out.length;
			}
			if (fitted.length === 0) {
				showToast('Not enough room left in the point limits for this outline.', 'error');
				return;
			}

			const name = preview.name.slice(0, 60);
			const newAreas: DraftArea[] = fitted.map((ring, i) => ({
				key: nextAreaKey(),
				name: fitted.length > 1 ? `${name} ${i + 1}` : name,
				ring,
			}));
			mutate((prev) => {
				if (replacing) {
					const list = [...prev];
					list.splice(activeIdx, 1, { ...newAreas[0], name: prev[activeIdx]?.name || newAreas[0].name }, ...newAreas.slice(1));
					return list;
				}
				return [...prev, ...newAreas];
			});
			setActiveIdx(replacing ? activeIdx : areas.length);
			setPreview(null);
			const used = fitted.reduce((n, r) => n + r.length, 0);
			showToast(
				used < original
					? `Outline loaded, simplified from ${original} to ${used} points. Adjust it if needed, then Save.`
					: `Outline loaded (${used} points). Adjust it if needed, then Save.`,
			);
		},
		[preview, areas, activeIdx, totalPoints, mutate, showToast],
	);

	// A previewed outline belongs to the org it was searched for.
	useEffect(() => {
		setPreview(null);
		setPlaceResults(null);
	}, [selectedKey]);

	// ── Persistence ──────────────────────────────────────────────────────────

	const save = useCallback(async () => {
		if (!selectedKey || !canSave) return;

		const payloadAreas = areas
			.filter((a) => a.ring.length > 0)
			.map((a) => ({ name: a.name, ring: a.ring }));

		// Saving nothing over something is how a perimeter gets silently wiped —
		// it looks like an ordinary Save. Make that one explicit.
		const hadPerimeter = (selectedOrg?.boundary?.areas?.length ?? 0) > 0;
		if (payloadAreas.length === 0 && hadPerimeter) {
			const ok = await confirm({
				title: `Remove ${selectedKey}'s perimeter?`,
				message: (
					<>
						This saves an <strong>empty</strong> perimeter, which switches the geofence off — reports to{' '}
						<strong>{selectedKey}</strong> will then be accepted from anywhere. The existing shape is not recoverable.
					</>
				),
				confirmLabel: 'Remove perimeter',
				danger: true,
			});
			if (!ok) return;
		}

		setSaving(true);
		try {
			const res = await fetch(`${BACKEND_URL}/users/organisation/${encodeURIComponent(selectedKey)}/boundary`, {
				method: 'PUT',
				credentials: 'include',
				headers: { 'Content-Type': 'application/json' },
				body: JSON.stringify({ areas: payloadAreas, bufferMeters, isActive: guardActive }),
			});
			const payload = await res.json().catch(() => null);
			if (!res.ok) {
				// HttpException built from a plain string replies with a bare JSON
				// string, not { message } — handle both so a geometry complaint
				// ("the perimeter crosses itself…") actually reaches the admin.
				const detail = typeof payload === 'string' ? payload : payload?.message;
				throw new Error(detail || `Save failed (HTTP ${res.status})`);
			}
			const org: OrgRow = payload.organisation;
			setOrgs((prev) => prev.map((o) => (o.key === selectedKey ? { ...o, ...org } : o)));
			setBaseline(fingerprint(toDraft(org.boundary), org.boundary.bufferMeters, org.boundary.isActive));
			showToast(
				payloadAreas.length === 0
					? 'Geofence cleared — reports accepted from anywhere.'
					: `Boundary saved (v${org.boundary.version}).`,
			);
		} catch (e: any) {
			showToast(e?.message || 'Save failed.', 'error');
		} finally {
			setSaving(false);
		}
	}, [selectedKey, canSave, areas, bufferMeters, guardActive, showToast, confirm, selectedOrg]);

	// ── Organisation create / edit ───────────────────────────────────────────

	const openCreateOrg = () => {
		setOrgMenuOpen(false);
		setOrgForm({ mode: 'create', key: '', fullName: '', file: null, preview: null });
	};

	const openEditOrg = () => {
		if (!selectedOrg) return;
		setOrgMenuOpen(false);
		setOrgForm({
			mode: 'edit',
			key: selectedOrg.key,
			fullName: selectedOrg.fullName,
			file: null,
			preview: logoSrc(selectedOrg),
		});
	};

	const pickLogo = (file: File | null) => {
		if (!file) return;
		if (file.size > MAX_LOGO_BYTES) {
			showToast('Logo must be under 2 MB.', 'error');
			return;
		}
		setOrgForm((f) => (f ? { ...f, file, preview: URL.createObjectURL(file) } : f));
	};

	const submitOrgForm = useCallback(async () => {
		if (!orgForm) return;
		const key = orgForm.key.trim();
		const fullName = orgForm.fullName.trim();
		if (orgForm.mode === 'create' && key.length < 2) {
			showToast('Short name must be at least 2 characters.', 'error');
			return;
		}
		if (fullName.length < 2) {
			showToast('Full name must be at least 2 characters.', 'error');
			return;
		}

		setSavingOrg(true);
		try {
			const body = new FormData();
			if (orgForm.mode === 'create') body.append('key', key);
			body.append('fullName', fullName);
			if (orgForm.file) body.append('logo', orgForm.file);

			const url =
				orgForm.mode === 'create'
					? `${BACKEND_URL}/users/organisation`
					: `${BACKEND_URL}/users/organisation/${encodeURIComponent(orgForm.key)}`;
			const res = await fetch(url, {
				method: orgForm.mode === 'create' ? 'POST' : 'PUT',
				credentials: 'include',
				body,
			});
			const payload = await res.json().catch(() => null);
			if (!res.ok) {
				const detail = typeof payload === 'string' ? payload : payload?.message;
				throw new Error(detail || `Request failed (HTTP ${res.status})`);
			}

			await loadOrganisations();
			if (orgForm.mode === 'create') setSelectedKey(key);
			showToast(orgForm.mode === 'create' ? `"${key}" created.` : 'Organisation updated.');
			setOrgForm(null);
		} catch (e: any) {
			showToast(e?.message || 'Could not save the organisation.', 'error');
		} finally {
			setSavingOrg(false);
		}
	}, [orgForm, loadOrganisations, showToast]);

	const deleteOrganisation = useCallback(async () => {
		if (!orgForm || orgForm.mode !== 'edit') return;
		const target = orgs.find((o) => o.key === orgForm.key);
		const count = target?.authorityCount ?? 0;

		const ok = await confirm({
			title: `Delete ${orgForm.key}?`,
			message: (
				<>
					<p className='m-0 mb-2'>
						{count > 0 ? (
							<>
								<strong>
									{count} {count === 1 ? 'authority' : 'authorities'}
								</strong>{' '}
								assigned to this organisation will be set to <strong>None</strong>.
							</>
						) : (
							'No authorities are assigned to this organisation.'
						)}
					</p>
					<p className='m-0'>
						Reports already filed are not affected — they keep their authority and full history, they just stop
						showing an organisation label.
					</p>
				</>
			),
			confirmLabel: 'Delete',
			danger: true,
		});
		if (!ok) return;

		setSavingOrg(true);
		try {
			const res = await fetch(`${BACKEND_URL}/users/organisation/${encodeURIComponent(orgForm.key)}`, {
				method: 'DELETE',
				credentials: 'include',
			});
			const payload = await res.json().catch(() => null);
			if (!res.ok) {
				const detail = typeof payload === 'string' ? payload : payload?.message;
				throw new Error(detail || `Delete failed (HTTP ${res.status})`);
			}
			const deletedKey = orgForm.key;
			setOrgForm(null);
			// Force a re-select: the deleted org is gone from the list.
			setSelectedKey('');
			await loadOrganisations();
			const n = payload?.authoritiesUnassigned ?? 0;
			showToast(
				n > 0
					? `"${deletedKey}" deleted · ${n} ${n === 1 ? 'authority' : 'authorities'} set to None.`
					: `"${deletedKey}" deleted.`,
			);
		} catch (e: any) {
			showToast(e?.message || 'Could not delete the organisation.', 'error');
		} finally {
			setSavingOrg(false);
		}
	}, [orgForm, orgs, confirm, loadOrganisations, showToast]);

	// ── Render ───────────────────────────────────────────────────────────────

	if (loading) {
		return (
			<div className='flex items-center justify-center py-24 text-gray-500'>
				<Loader2 className='w-6 h-6 mr-3 animate-spin' />
				Loading organisations…
			</div>
		);
	}

	if (loadError) {
		return (
			<div className='max-w-6xl mx-auto px-4 pb-5'>
				<div className='max-w-xl mx-auto bg-white rounded-2xl shadow-sm border border-red-100 p-8 text-center'>
					<AlertTriangle className='w-8 h-8 text-red-500 mx-auto mb-3' />
					<p className='text-gray-700 mb-5'>{loadError}</p>
					<button
						onClick={loadOrganisations}
						className='rounded-xl bg-blue-600 px-4 py-2 font-bold text-white hover:bg-blue-700'
					>
						Retry
					</button>
				</div>
			</div>
		);
	}

	const activePointCount = activeArea?.ring.length ?? 0;
	const nearCap = activePointCount >= MAX_POINTS_PER_AREA * 0.8;
	const selectedLogo = logoSrc(selectedOrg);

	const toolbarBtn =
		'inline-flex items-center gap-1.5 rounded-xl border border-gray-200 px-3 h-9 text-sm font-semibold text-gray-700 hover:bg-gray-50 disabled:opacity-40 disabled:hover:bg-white';

	return (
		<>
			{/* Toast */}
			{toast && (
				<div
					className={`fixed bottom-6 right-6 z-[9999] flex items-center gap-3 px-5 py-3.5 rounded-2xl shadow-2xl border transition-all duration-300 ${toast.type === 'error' ? 'bg-red-600 border-red-500 text-white' : 'bg-gray-900 border-gray-700 text-white'
						}`}
				>
					{toast.type === 'error' ? (
						<AlertTriangle className='w-4 h-4 shrink-0' />
					) : (
						<Check className='w-4 h-4 shrink-0 text-emerald-400' />
					)}
					<span className='text-sm font-semibold'>{toast.message}</span>
				</div>
			)}

			<div className='max-w-6xl mx-auto px-4 pb-5'>
				<h1 className='text-3xl font-extrabold text-blue-900 mb-8'>Organisations</h1>

				<div className='grid grid-cols-1 xl:grid-cols-[320px_1fr] gap-6 items-start'>
					{/* ── Left rail ─────────────────────────────────────────────── */}
					<div className='space-y-6'>
						{/* Organisation picker */}
						<RailCard
							title='Organisation'
							action={
								<button
									onClick={openCreateOrg}
									className='inline-flex items-center gap-1 text-xs font-bold text-blue-700 hover:text-blue-900'
								>
									<Plus className='w-3.5 h-3.5' /> Add new
								</button>
							}
						>
							<div className='relative' ref={orgMenuRef}>
								<button
									onClick={() => setOrgMenuOpen((v) => !v)}
									className='w-full flex items-center justify-between gap-2 rounded-xl border border-gray-200 px-3 py-2.5 text-left hover:border-blue-300 transition-colors'
								>
									<span className='flex items-center gap-2 min-w-0'>
										<OrgLogo src={selectedLogo} size={24} />
										<span className='min-w-0'>
											<span className='block font-bold text-gray-900 truncate'>{selectedOrg?.key || 'Select…'}</span>
											<span className='block text-xs text-gray-500 truncate'>{selectedOrg?.fullName || ''}</span>
										</span>
									</span>
									<ChevronDown
										className={`w-4 h-4 text-gray-400 shrink-0 transition-transform ${orgMenuOpen ? 'rotate-180' : ''}`}
									/>
								</button>
								{orgMenuOpen && (
									<div className='absolute left-0 right-0 top-full mt-1 bg-white border border-gray-200 rounded-xl shadow-lg z-[1000] overflow-hidden max-h-72 overflow-y-auto'>
										{orgs.map((o) => {
											const src = logoSrc(o);
											const mapped = o.boundary?.areas?.length > 0;
											return (
												<button
													key={o.key}
													onClick={() => selectOrg(o.key)}
													className={`w-full text-left px-3 py-2.5 hover:bg-blue-50 transition-colors ${o.key === selectedKey ? 'bg-blue-50/60' : ''}`}
												>
													<span className='flex items-center gap-2'>
														<OrgLogo src={src} size={24} />
														<span className='min-w-0 flex-1'>
															<span className='block font-bold text-sm text-gray-900 truncate'>{o.key}</span>
															<span className='block text-xs text-gray-500 truncate'>{o.fullName}</span>
														</span>
														<span
															className={`shrink-0 text-[10px] font-bold uppercase rounded-full px-2 py-0.5 ${mapped ? 'text-emerald-700 bg-emerald-50' : 'text-gray-500 bg-gray-100'}`}
														>
															{mapped ? 'Mapped' : 'Unmapped'}
														</span>
													</span>
												</button>
											);
										})}
									</div>
								)}
							</div>

							<div className='flex items-center justify-between gap-2 mt-3'>
								<p className='text-xs text-gray-500 text-left m-0'>
									{selectedOrg?.boundary?.updatedAt
										? `Saved v${selectedOrg.boundary.version} · ${formatDateTime(selectedOrg.boundary.updatedAt)}`
										: 'Not saved yet'}
								</p>
								<button
									onClick={openEditOrg}
									disabled={!selectedOrg}
									className='inline-flex items-center gap-1 text-xs font-bold text-gray-600 hover:text-gray-900 disabled:text-gray-300'
								>
									<Pencil className='w-3.5 h-3.5' /> Edit
								</button>
							</div>
						</RailCard>

						{/* Areas */}
						<RailCard
							title={`Areas (${areas.length}/${MAX_AREAS})`}
							action={
								<button
									onClick={addArea}
									disabled={areas.length >= MAX_AREAS}
									className='inline-flex items-center gap-1 text-xs font-bold text-blue-700 hover:text-blue-900 disabled:text-gray-300'
								>
									<Plus className='w-3.5 h-3.5' /> Add area
								</button>
							}
						>
							{areas.length === 0 ? (
								<p className='text-sm text-gray-500 leading-relaxed text-left m-0'>
									No perimeter yet. Search the campus above to load its outline, or click on the map to drop the first point.
								</p>
							) : (
								<div className='space-y-2'>
									{areas.map((a, i) => (
										<div
											key={a.key}
											onClick={() => setActiveIdx(i)}
											className={`flex items-center justify-between gap-2 rounded-xl border px-3 py-2 cursor-pointer transition-colors ${i === activeIdx ? 'border-blue-300 bg-blue-50/60' : 'border-gray-200 hover:bg-gray-50'}`}
										>
											<span className='min-w-0'>
												<span className='block text-sm font-bold text-gray-900 truncate text-left'>
													{a.name?.trim() || `Area ${i + 1}`}
												</span>
												<span className='block text-xs text-gray-500 text-left'>
													{a.ring.length} pts{/* · {formatArea(ringArea(a.ring))} */}
												</span>
											</span>
											<span className='flex items-center gap-1 shrink-0'>
												<button
													onClick={(e) => {
														e.stopPropagation();
														setActiveIdx(i);
														fitToPoints(a.ring);
													}}
													disabled={a.ring.length === 0}
													title='Zoom to this area'
													className='p-1 rounded-lg text-gray-400 hover:text-blue-600 hover:bg-blue-50 disabled:text-gray-200 disabled:hover:bg-transparent'
												>
													<Crosshair className='w-4 h-4' />
												</button>
												{/* Two-step: × arms, ✓ commits. */}
												{armedAreaKey === a.key ? (
													<button
														data-armed-btn='true'
														onClick={(e) => {
															e.stopPropagation();
															deleteArea(i);
														}}
														title='Click again to confirm'
														className='p-1 rounded-lg text-white bg-red-600 hover:bg-red-700'
													>
														<Check className='w-4 h-4' />
													</button>
												) : (
													<button
														onClick={(e) => {
															e.stopPropagation();
															setArmedAreaKey(a.key);
														}}
														title='Delete this area'
														className='p-1 rounded-lg text-gray-400 hover:text-red-600 hover:bg-red-50'
													>
														<X className='w-4 h-4' />
													</button>
												)}
											</span>
										</div>
									))}
								</div>
							)}
						</RailCard>

						{/* This area */}
						<RailCard title='Points'>
							<dl className='space-y-2 text-sm m-0'>
								<div className='flex items-center justify-between gap-2'>
									<dt className='text-gray-500'>Points</dt>
									<dd
										className={`font-bold m-0 ${activePointCount >= MAX_POINTS_PER_AREA ? 'text-red-600' : nearCap ? 'text-amber-600' : 'text-gray-900'}`}
									>
										{activePointCount} / {MAX_POINTS_PER_AREA} pts
									</dd>
								</div>
								{/* <div className='flex items-center justify-between gap-2'>
									<dt className='text-gray-500'>Enclosed area</dt>
									<dd className='font-bold text-gray-900 m-0'>{formatArea(ringArea(activeArea?.ring ?? []))}</dd>
								</div>
								<div className='flex items-center justify-between gap-2'>
									<dt className='text-gray-500'>Perimeter</dt>
									<dd className='font-bold text-gray-900 m-0'>{formatLength(ringPerimeter(activeArea?.ring ?? []))}</dd>
								</div> */}
								<div className='flex items-center justify-between gap-2 pt-2 border-t border-gray-100'>
									<dt className='text-gray-500'>All areas</dt>
									<dd className='font-bold text-gray-900 m-0'>
										{totalPoints} / {MAX_TOTAL_POINTS} pts
									</dd>
								</div>
							</dl>
						</RailCard>

						{/* Guard settings */}
						<RailCard title='Guard settings'>
							<div className='space-y-4'>
								<div>
									<div className='flex items-center justify-between gap-3 mb-1.5'>
										<span className='text-sm font-semibold text-gray-700 text-left'>Margin outside boundary</span>
										<div className='flex items-center gap-1.5 shrink-0'>
											<input
												type='number'
												min={0}
												max={MAX_BUFFER_METERS}
												value={bufferMeters}
												onChange={(e) => {
													const n = Number(e.target.value);
													setBufferMeters(
														Number.isFinite(n) ? Math.max(0, Math.min(MAX_BUFFER_METERS, Math.round(n))) : 0,
													);
												}}
												className='w-20 h-8 rounded-lg border border-gray-200 px-2 text-sm text-right focus:border-blue-400 focus:outline-none'
											/>
											<span className='text-sm text-gray-500'>m</span>
										</div>
									</div>
									<p className='text-xs text-gray-500 leading-relaxed text-left m-0'>
										Phone GPS is often off by 10–20 m. A photo taken up to this far <em>outside</em> the line still counts
										as on campus, so someone reporting from just inside the gate isn't turned away. Suggested: 20 m. 0 = the
										exact line.
									</p>
								</div>

								<div className='pt-3 border-t border-gray-100'>
									<div className='flex items-center justify-between gap-3 mb-1.5'>
										<span className='text-sm font-semibold text-gray-700 text-left'>Enforce geofence</span>
										<button
											onClick={() => setGuardActive((v) => !v)}
											className={`shrink-0 w-14 h-8 rounded-full transition-colors relative ${guardActive ? 'bg-emerald-500' : 'bg-gray-300'}`}
											aria-label='Toggle enforcement'
										>
											<span
												className={`absolute top-1 w-6 h-6 bg-white rounded-full shadow transition-all ${guardActive ? 'left-7' : 'left-1'}`}
											/>
										</button>
									</div>
									<p className='text-xs text-gray-500 leading-relaxed text-left m-0'>
										ON: reports only from inside the boundary. The app offers this organisation only to people inside it (and picks it for them), and the server rejects reports whose photos were taken outside.<br></br>OFF: reports from anywhere, for organisations spread across the country (e.g. police). The drawn boundary is kept.
									</p>
								</div>
							</div>
						</RailCard>
					</div>

					{/* ── Map + toolbar ─────────────────────────────────────────── */}
					<div className='bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden flex flex-col'>
						{/* Toolbar */}
						<div className='p-3 border-b border-gray-100 space-y-2.5'>
							{/* Top row: Place search */}
							<div className='flex items-center gap-2'>
								<div className='relative flex-1' ref={searchBoxRef}>
									<Search className='w-4 h-4 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none' />
									<input
										value={searchText}
										onChange={(e) => setSearchText(e.target.value)}
										onKeyDown={(e) => {
											if (e.key === 'Enter') searchPlace();
											if (e.key === 'Escape') setPlaceResults(null);
										}}
										placeholder='Find a campus or place, e.g. IIT Delhi'
										className='w-full h-9 rounded-xl border border-gray-200 pl-9 pr-3 text-sm focus:border-blue-400 focus:outline-none'
									/>
									{placeResults && (
										<div className='absolute left-0 right-0 top-full mt-1 bg-white border border-gray-200 rounded-xl shadow-lg z-[1100] overflow-hidden'>
											{placeResults.map((r) => (
												<button
													key={r.id}
													onClick={() => choosePlace(r)}
													className='w-full flex items-start gap-2.5 text-left px-3 py-2.5 hover:bg-blue-50 border-b border-gray-100 last:border-b-0'
												>
													{r.rings.length > 0 ? (
														<Shapes className='w-4 h-4 mt-0.5 shrink-0 text-emerald-600' />
													) : (
														<MapPin className='w-4 h-4 mt-0.5 shrink-0 text-gray-400' />
													)}
													<span className='min-w-0 flex-1'>
														<span className='block text-sm font-bold text-gray-900 truncate'>{r.name}</span>
														<span className='block text-xs text-gray-500 truncate'>{r.detail}</span>
													</span>
													<span
														className={`shrink-0 text-[10px] font-bold uppercase rounded-full px-2 py-0.5 ${
															r.rings.length > 0 ? 'text-emerald-700 bg-emerald-50' : 'text-gray-500 bg-gray-100'
														}`}
													>
														{r.rings.length > 0 ? 'Outline' : 'Point only'}
													</span>
												</button>
											))}
											<div className='px-3 py-1.5 text-[11px] text-gray-400 bg-gray-50 text-left'>
												Outlines from OpenStreetMap. Places marked "Point only" have no mapped boundary.
											</div>
										</div>
									)}
								</div>
								<button
									onClick={searchPlace}
									disabled={searching || !searchText.trim()}
									title='Search'
									aria-label='Search'
									className='inline-flex items-center justify-center h-9 px-4 rounded-xl bg-blue-600 font-semibold text-sm text-white hover:bg-blue-700 disabled:bg-gray-200 disabled:text-gray-400 transition-colors shrink-0 gap-1.5'
								>
									{searching ? <Loader2 className='w-4 h-4 animate-spin' /> : <Search className='w-4 h-4' />}
									<span>Search</span>
								</button>
							</div>

							{/* Bottom row: Tool action buttons */}
							<div className='flex flex-wrap items-center gap-2'>
								<button onClick={removeLastPoint} disabled={activePointCount === 0} title='Remove the last point placed in this area' className={toolbarBtn}>
									<RotateCcw className='w-4 h-4' /> Remove last point
								</button>
								<button onClick={undo} disabled={past.length === 0} title='Undo (Ctrl+Z)' className={toolbarBtn}>
									<Undo2 className='w-4 h-4' /> Undo
								</button>
								<button onClick={redo} disabled={future.length === 0} title='Redo (Ctrl+Y)' className={toolbarBtn}>
									<Redo2 className='w-4 h-4' /> Redo
								</button>

								<span className='mx-1 h-6 w-px bg-gray-200' />

								<button onClick={() => setSatellite((v) => !v)} title='Switch basemap' className={toolbarBtn}>
									<Layers className='w-4 h-4' /> {satellite ? 'Satellite' : 'Street'}
								</button>
								<button onClick={fitToPolygon} disabled={totalPoints === 0} title='Zoom to every area' className={toolbarBtn}>
									<Crosshair className='w-4 h-4' /> Fit all
								</button>
							</div>
						</div>

						{/* Which organisation these edits belong to. The picker sits in
						    the far rail and the org list is alphabetical, so it is very
						    easy to spend ten minutes drawing into the wrong one. */}
						<div className='flex items-center gap-2 px-3 py-2 bg-gray-50 border-b border-gray-200'>
							<OrgLogo src={selectedLogo} size={20} />
							<span className='text-xs text-gray-500'>Drawing the perimeter for</span>
							<span className='text-sm font-extrabold text-gray-900 truncate'>{selectedOrg?.key || '—'}</span>
							<span className='text-xs text-gray-400 truncate hidden sm:inline'>{selectedOrg?.fullName || ''}</span>
						</div>

						{/* Hint bar */}
						<div className='flex items-start gap-2 px-3 py-2 bg-blue-50/60 border-b border-blue-100 text-xs text-blue-900'>
							<Info className='w-3.5 h-3.5 mt-0.5 shrink-0' />
							<span className='text-left'>
								<strong>Search</strong> a campus above to load its outline, or draw by hand: <strong>Click</strong> to add a point <strong>· Drag</strong> a point to move it <strong>· Click a point</strong> to delete it <strong>· Click the midpoint</strong> of an edge to insert a point there
							</span>
						</div>

						{/* Map */}
						<div className='relative' style={{ height: 'min(62vh, 620px)' }}>
							<MapContainer
								center={DEFAULT_CENTER}
								zoom={DEFAULT_ZOOM}
								style={{ height: '100%', width: '100%' }}
								doubleClickZoom={false}
								maxZoom={20}
							>
								<MapHandle onReady={onMapReady} />
								<CtrlScrollZoom />
								{satellite ? (
									<TileLayer
										url='https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}'
										attribution='Imagery &copy; Esri'
										maxZoom={20}
										maxNativeZoom={19}
									/>
								) : (
									<TileLayer
										url='https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png'
										attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
										maxZoom={20}
										maxNativeZoom={19}
									/>
								)}
								<ClickToAdd onAdd={addPoint} />

								{/* Outline being previewed from search — not part of the draft yet. */}
								{preview?.rings.map((ring, i) => (
									<Polygon
										key={`preview-${i}`}
										positions={ring.map((p) => [p.lat, p.lng] as [number, number])}
										interactive={false}
										pathOptions={{ color: '#f59e0b', weight: 3, dashArray: '8 6', fillColor: '#f59e0b', fillOpacity: 0.12 }}
									/>
								))}

								{areas.map((area, ai) => {
									const isActive = ai === activeIdx;
									const positions = area.ring.map((p) => [p.lat, p.lng] as [number, number]);
									const colour = isActive ? '#2563eb' : '#64748b';
									return (
										<React.Fragment key={area.key}>
											{positions.length >= 3 ? (
												<Polygon
													positions={positions}
													pathOptions={{
														color: colour,
														weight: isActive ? 3 : 2,
														fillColor: colour,
														fillOpacity: isActive ? 0.18 : 0.08,
														dashArray: isActive ? undefined : '6 6',
													}}
													eventHandlers={{ click: () => setActiveIdx(ai) }}
												/>
											) : positions.length === 2 ? (
												<Polyline positions={positions} pathOptions={{ color: colour, weight: 3 }} />
											) : null}

											{/* Vertex handles — active area only, to keep the map readable. */}
											{isActive &&
												area.ring.map((p, vi) => (
													<Marker
														key={`v-${vi}`}
														position={[p.lat, p.lng]}
														icon={vi === 0 ? firstVertexIcon : vertexIcon}
														draggable
														title={`Point ${vi + 1} — drag to move, click to delete`}
														eventHandlers={{
															click: () => removeVertex(ai, vi),
															dragstart: (e: any) => {
																const map = mapRef.current;
																if (!map) return;
																const n = area.ring.length;
																if (n < 2) return;
																const prev = area.ring[(vi - 1 + n) % n];
																const next = area.ring[(vi + 1) % n];
																const ll = e.target.getLatLng();
																const opts = { color: '#93c5fd', weight: 3 };
																if (n === 2) {
																	const line = L.polyline([[prev.lat, prev.lng], [ll.lat, ll.lng]], opts).addTo(map);
																	dragLinesRef.current = [line];
																} else {
																	const l1 = L.polyline([[prev.lat, prev.lng], [ll.lat, ll.lng]], opts).addTo(map);
																	const l2 = L.polyline([[ll.lat, ll.lng], [next.lat, next.lng]], opts).addTo(map);
																	dragLinesRef.current = [l1, l2];
																}
															},
															drag: (e: any) => {
																const ll = e.target.getLatLng();
																const n = area.ring.length;
																if (dragLinesRef.current.length === 1) {
																	const prev = area.ring[(vi - 1 + n) % n];
																	dragLinesRef.current[0].setLatLngs([[prev.lat, prev.lng], [ll.lat, ll.lng]]);
																} else if (dragLinesRef.current.length === 2) {
																	const prev = area.ring[(vi - 1 + n) % n];
																	const next = area.ring[(vi + 1) % n];
																	dragLinesRef.current[0].setLatLngs([[prev.lat, prev.lng], [ll.lat, ll.lng]]);
																	dragLinesRef.current[1].setLatLngs([[ll.lat, ll.lng], [next.lat, next.lng]]);
																}
															},
															dragend: (e: any) => {
																var lines = dragLinesRef.current;
																dragLinesRef.current = [];
																lines.forEach(l => l.remove());
																const ll = e.target.getLatLng();
																moveVertex(ai, vi, { lat: ll.lat, lng: ll.lng });
															},
														}}
													/>
												))}

											{/* Midpoint handles for inserting between two points. */}
											{isActive &&
												area.ring.length >= 2 &&
												area.ring.map((p, vi) => {
													const q = area.ring[(vi + 1) % area.ring.length];
													// Skip the closing edge until the ring is a real polygon.
													if (vi === area.ring.length - 1 && area.ring.length < 3) return null;
													const mid: LatLng = { lat: (p.lat + q.lat) / 2, lng: (p.lng + q.lng) / 2 };
													return (
														<Marker
															key={`m-${vi}`}
															position={[mid.lat, mid.lng]}
															icon={midpointIcon}
															title='Insert a point here'
															eventHandlers={{ click: () => insertVertex(ai, vi, mid) }}
														/>
													);
												})}
										</React.Fragment>
									);
								})}
							</MapContainer>

							{preview && (
								<div className='absolute top-3 left-3 right-3 z-[1000] bg-white/95 backdrop-blur rounded-xl shadow-lg border border-amber-200 px-3 py-2.5 flex flex-wrap items-center gap-2'>
									<Shapes className='w-4 h-4 text-amber-600 shrink-0' />
									<span className='text-sm text-gray-800 text-left flex-1 min-w-[12rem]'>
										Outline of <strong>{preview.name}</strong> ·{' '}
										{preview.rings.reduce((n, r) => n + r.length, 0)} points
										{preview.rings.length > 1 ? ` in ${preview.rings.length} parts` : ''}
									</span>
									{areas.some((a) => a.ring.length > 0) ? (
										<>
											<button
												onClick={() => applyOutline('replace')}
												className='h-8 px-3 rounded-lg bg-blue-600 text-white text-sm font-semibold hover:bg-blue-700'
											>
												Replace {areas[activeIdx]?.name?.trim() || `Area ${activeIdx + 1}`}
											</button>
											<button
												onClick={() => applyOutline('add')}
												disabled={areas.length >= MAX_AREAS}
												className='h-8 px-3 rounded-lg border border-gray-200 text-sm font-semibold text-gray-700 hover:bg-gray-50 disabled:opacity-40'
											>
												Add as new area
											</button>
										</>
									) : (
										<button
											onClick={() => applyOutline('replace')}
											className='h-8 px-3 rounded-lg bg-blue-600 text-white text-sm font-semibold hover:bg-blue-700'
										>
											Use this outline
										</button>
									)}
									<button
										onClick={() => setPreview(null)}
										className='h-8 px-3 rounded-lg text-sm font-semibold text-gray-500 hover:bg-gray-100'
									>
										Cancel
									</button>
								</div>
							)}
						</div>

						{/* Footer: problems + actions */}
						<div className='p-3 border-t border-gray-100 space-y-3'>
							{problems.length > 0 && (
								<div className='rounded-xl bg-amber-50 border border-amber-200 px-3 py-2'>
									<div className='flex items-center gap-1.5 text-amber-900 font-bold text-xs mb-1'>
										<AlertTriangle className='w-3.5 h-3.5' /> Fix before saving
									</div>
									<ul className='text-xs text-amber-900 list-disc pl-5 space-y-0.5 mb-0 text-left'>
										{problems.map((p, i) => (
											<li key={i}>{p}</li>
										))}
									</ul>
								</div>
							)}

							<div className='flex flex-wrap items-center gap-2'>
								<button
									onClick={save}
									disabled={!canSave}
									className='inline-flex items-center gap-2 rounded-xl bg-blue-600 px-4 h-9 font-bold text-white hover:bg-blue-700 disabled:bg-gray-200 disabled:text-gray-400'
								>
									{saving ? <Loader2 className='w-4 h-4 animate-spin' /> : <Save className='w-4 h-4' />}
									Save boundary
								</button>
								<button
									onClick={discardChanges}
									disabled={!dirty || saving}
									className='inline-flex items-center gap-2 rounded-xl border border-gray-200 px-4 h-9 font-bold text-gray-700 hover:bg-gray-50 disabled:opacity-40 disabled:hover:bg-white'
								>
									Discard changes
								</button>

								<span className={`ml-auto text-sm font-semibold ${dirty ? 'text-amber-600' : 'text-gray-400'}`}>
									{dirty ? 'Unsaved changes' : 'All changes saved'}
								</span>
							</div>

							{areas.length === 0 && dirty && (
								<p className='text-xs text-gray-500 text-left m-0'>
									Saving with no areas removes the geofence — reports to this organisation will be accepted from
									anywhere.
								</p>
							)}
						</div>
					</div>
				</div>
			</div>

			{/* ── Add / edit organisation ─────────────────────────────────────── */}
			{orgForm && (
				<div
					className='fixed inset-0 z-[10000] bg-black/40 flex items-center justify-center p-4'
					onClick={() => !savingOrg && setOrgForm(null)}
				>
					<div
						className='bg-white rounded-2xl shadow-2xl border border-gray-100 w-full max-w-md p-6'
						onClick={(e) => e.stopPropagation()}
					>
						<div className='flex items-center justify-between gap-2 mb-5'>
							<h2 className='text-xl font-extrabold text-blue-900 m-0'>
								{orgForm.mode === 'create' ? 'Add organisation' : 'Edit organisation'}
							</h2>
							<button
								onClick={() => setOrgForm(null)}
								disabled={savingOrg}
								className='text-gray-400 hover:text-gray-700 disabled:opacity-40'
							>
								<X className='w-5 h-5' />
							</button>
						</div>

						<div className='space-y-4'>
							<div>
								<label className='block text-xs font-bold uppercase tracking-wide text-gray-500 mb-1.5 text-left'>
									Short name
								</label>
								<input
									value={orgForm.key}
									disabled={orgForm.mode === 'edit'}
									onChange={(e) => setOrgForm((f) => (f ? { ...f, key: e.target.value } : f))}
									placeholder='e.g. IIT Delhi'
									className='w-full h-10 rounded-xl border border-gray-200 px-3 text-sm focus:border-blue-400 focus:outline-none disabled:bg-gray-50 disabled:text-gray-500'
								/>
								<p className='text-xs text-gray-500 mt-1.5 mb-0 text-left leading-relaxed'>
									{orgForm.mode === 'create'
										? 'Shown in the app picker and stored on every authority in this organisation. It cannot be changed later.'
										: 'Fixed: authorities are matched on this exact value.'}
								</p>
							</div>

							<div>
								<label className='block text-xs font-bold uppercase tracking-wide text-gray-500 mb-1.5 text-left'>
									Full name
								</label>
								<input
									value={orgForm.fullName}
									onChange={(e) => setOrgForm((f) => (f ? { ...f, fullName: e.target.value } : f))}
									placeholder='e.g. Indian Institute of Technology, Delhi'
									className='w-full h-10 rounded-xl border border-gray-200 px-3 text-sm focus:border-blue-400 focus:outline-none'
								/>
							</div>

							<div>
								<label className='block text-xs font-bold uppercase tracking-wide text-gray-500 mb-1.5 text-left'>
									Logo
								</label>
								<div className='flex items-center gap-3'>
									{orgForm.preview ? (
										<OrgLogo src={orgForm.preview} size={56} className='border border-gray-200' />
									) : (
										<div className='w-14 h-14 rounded-full border border-dashed border-gray-300 flex items-center justify-center text-gray-300 shrink-0'>
											<Building2 className='w-6 h-6' />
										</div>
									)}
									<label className='inline-flex items-center gap-2 rounded-xl border border-gray-200 px-3 h-9 text-sm font-semibold text-gray-700 hover:bg-gray-50 cursor-pointer'>
										<ImagePlus className='w-4 h-4' />
										{orgForm.preview ? 'Replace' : 'Choose image'}
										<input
											type='file'
											accept='image/png,image/jpeg,image/webp'
											className='hidden'
											onChange={(e) => pickLogo(e.target.files?.[0] ?? null)}
										/>
									</label>
								</div>
								<p className='text-xs text-gray-500 mt-2 mb-0 text-left leading-relaxed'>
									PNG, JPEG or WebP, under 2 MB. Shown next to the organisation in the app.
								</p>
							</div>
						</div>

						{orgForm.mode === 'edit' && (
							<p className='text-xs text-gray-500 mt-5 mb-0 text-left'>
								Last updated {formatDateTime(orgs.find((o) => o.key === orgForm.key)?.boundary?.updatedAt) || '—'}
							</p>
						)}

						<div className='flex items-center gap-2 mt-6'>
							{orgForm.mode === 'edit' && (
								<button
									onClick={deleteOrganisation}
									disabled={savingOrg}
									className='inline-flex items-center gap-2 rounded-xl border border-red-200 px-3 h-9 font-bold text-red-700 hover:bg-red-50 disabled:opacity-40 mr-auto'
								>
									<Trash2 className='w-4 h-4' /> Delete
								</button>
							)}
							<button
								onClick={() => setOrgForm(null)}
								disabled={savingOrg}
								className={`rounded-xl border border-gray-200 px-4 h-9 font-bold text-gray-700 hover:bg-gray-50 disabled:opacity-40 ${orgForm.mode === 'create' ? 'ml-auto' : ''}`}
							>
								Cancel
							</button>
							<button
								onClick={submitOrgForm}
								disabled={savingOrg}
								className='inline-flex items-center gap-2 rounded-xl bg-blue-600 px-4 h-9 font-bold text-white hover:bg-blue-700 disabled:bg-gray-200 disabled:text-gray-400'
							>
								{savingOrg ? <Loader2 className='w-4 h-4 animate-spin' /> : <Check className='w-4 h-4' />}
								{orgForm.mode === 'create' ? 'Create' : 'Save'}
							</button>
						</div>
					</div>
				</div>
			)}

			{/* Site-styled replacement for window.confirm — rendered last so it
			    stacks above the organisation modal. */}
			{confirmDialog}
		</>
	);
};

export default OrganisationMapping;
